
// Legal content for the public policy pages (bilingual).
// Kept separate from translations.ts so each file stays maintainable.

export interface LegalSection {
  title: string;
  body: string;
  items?: string[];
}

export interface LegalPageContent {
  eyebrow: string;
  title: string;
  updated: string;
  intro: string;
  sections: LegalSection[];
  contactTitle: string;
  contactIntro: string;
  backHome: string;
}

interface LegalDict {
  labels: {
    privacy: string;
    terms: string;
    deletion: string;
    legalTitle: string;
  };
  privacy: LegalPageContent;
  terms: LegalPageContent;
  deletion: LegalPageContent;
}

const LEGAL_EMAIL = "antoniocioffir@hotmail.com";

export const legal: { es: LegalDict; en: LegalDict } = {
  es: {
    labels: {
      privacy: "Política de Privacidad",
      terms: "Términos de Servicio",
      deletion: "Eliminar Datos",
      legalTitle: "Legal",
    },
    privacy: {
      eyebrow: "Información Legal",
      title: "Política de Privacidad",
      updated: "Última actualización: 27 de agosto de 2026",
      intro:
        "El responsable del tratamiento de los datos es Granzonera y Agregados San Antonio, C.A. (GRANSACA). Esta política explica qué información recibe y procesa nuestro sistema de registro administrativo, con qué finalidad la utilizamos, con cuáles servicios tecnológicos se procesa y cómo puede ejercer sus derechos sobre sus datos.",
      sections: [
        {
          title: "1. Responsable del tratamiento",
          body: "El responsable del tratamiento de los datos personales descritos en esta política es Granzonera y Agregados San Antonio, C.A. (GRANSACA), empresa dedicada a la producción y comercialización de agregados para la construcción en Venezuela. Cualquier consulta relacionada con esta política puede dirigirse al correo indicado en la sección de contacto.",
        },
        {
          title: "2. Origen de la información",
          body: "El sistema recibe únicamente los mensajes enviados de forma voluntaria por el usuario al WhatsApp financiero de la empresa. No recolectamos datos de manera automática desde otras fuentes, ni adquirimos bases de datos de terceros. Si usted no envía mensajes a ese canal, el sistema no procesa información suya.",
        },
        {
          title: "3. Datos que se procesan",
          body: "En función de los mensajes enviados al WhatsApp financiero, el sistema puede procesar y almacenar los siguientes datos:",
          items: [
            "Nombre del usuario.",
            "Número telefónico asociado al mensaje.",
            "Contenido de los mensajes enviados.",
            "Registros de ventas.",
            "Registros de gastos.",
            "Registros de pagos y métodos de pago.",
            "Balances y saldos derivados de esos registros.",
          ],
        },
        {
          title: "4. Finalidad del tratamiento",
          body: "Los datos se utilizan exclusivamente para el control administrativo, contable y financiero interno de la empresa. Esto incluye el registro de movimientos, la conciliación de pagos, el seguimiento de ventas y gastos y la elaboración de balances internos. No se utilizan para ninguna finalidad distinta a la aquí declarada.",
        },
        {
          title: "5. Servicios tecnológicos utilizados",
          body: "Para recibir, procesar y almacenar la información, el sistema puede apoyarse en los siguientes servicios tecnológicos de terceros, cada uno con sus propias políticas de privacidad y seguridad:",
          items: [
            "Meta / WhatsApp — recepción de los mensajes enviados al WhatsApp financiero.",
            "Make — automatización del flujo de procesamiento de los mensajes.",
            "Google Sheets — almacenamiento estructurado de los registros administrativos.",
            "OpenAI — interpretación y estructuración del contenido de los mensajes.",
          ],
        },
        {
          title: "6. No comercialización de datos",
          body: "No vendemos, alquilamos ni cedemos los datos a terceros con fines comerciales. Tampoco utilizamos la información para publicidad, segmentación de audiencias, mercadeo directo ni perfilado promocional de ningún tipo.",
        },
        {
          title: "7. Seguridad y acceso restringido",
          body: "El acceso a la información está restringido al personal autorizado de la empresa que requiere los datos para cumplir funciones administrativas, contables o financieras. Se aplican medidas razonables de control de acceso, autenticación y resguardo de la información. Ningún medio de transmisión por internet es completamente infalible, por lo que no puede garantizarse una seguridad absoluta.",
        },
        {
          title: "8. Tiempo de conservación",
          body: "Los registros administrativos, contables y financieros se conservan mientras se mantenga la relación comercial u operativa y, posteriormente, durante el plazo exigido por las obligaciones contables, fiscales y legales aplicables en Venezuela. El contenido de los mensajes que no resulte necesario para sustentar un registro contable se elimina o anonimiza cuando deja de ser requerido para la finalidad que motivó su recepción.",
        },
        {
          title: "9. Derechos del usuario",
          body: "Usted puede solicitar en cualquier momento el acceso a sus datos, su corrección o actualización, así como su eliminación. Para ello, envíe su solicitud al correo de contacto indicando su nombre y el número telefónico desde el cual escribió al WhatsApp financiero. La solicitud será revisada y atendida, con excepción de los registros que deban conservarse por obligaciones legales o contables.",
        },
        {
          title: "10. Cambios en esta política",
          body: "Esta política puede actualizarse para reflejar cambios operativos, tecnológicos o normativos. Cualquier modificación se publicará en esta misma página, junto con su fecha de última actualización.",
        },
      ],
      contactTitle: "Contacto para asuntos de privacidad",
      contactIntro:
        "Para ejercer sus derechos o realizar consultas sobre esta política, escríbanos a:",
      backHome: "Volver al inicio",
    },
    terms: {
      eyebrow: "Información Legal",
      title: "Términos de Servicio",
      updated: "Última actualización: 27 de agosto de 2026",
      intro:
        "El sistema de registro administrativo de Granzonera y Agregados San Antonio, C.A. (GRANSACA) es una herramienta interna de uso restringido. Estos términos describen su propósito, quiénes pueden utilizarlo y las responsabilidades de cada usuario autorizado.",
      sections: [
        {
          title: "1. Naturaleza del sistema",
          body: "El sistema es una herramienta interna de registro administrativo, contable y financiero. Su función es recibir, ordenar y consolidar movimientos operativos de la empresa. No constituye un servicio comercial ofrecido al público, ni una plataforma de pagos, ni un sistema de asesoría contable, fiscal o financiera.",
        },
        {
          title: "2. Uso restringido a personas autorizadas",
          body: "Solo pueden utilizar el sistema las personas expresamente autorizadas por la empresa para registrar o consultar movimientos. El acceso es personal y no transferible. Queda prohibido compartir credenciales o permitir el uso del canal a terceros no autorizados. La empresa puede revocar la autorización en cualquier momento.",
        },
        {
          title: "3. Responsabilidad sobre la información suministrada",
          body: "Cada usuario es responsable de proporcionar información correcta, completa y oportuna. Esto incluye montos, fechas, conceptos, métodos de pago y cualquier dato necesario para identificar el movimiento. El usuario debe abstenerse de registrar información falsa, duplicada o que no corresponda a operaciones reales de la empresa.",
        },
        {
          title: "4. Confirmación de los movimientos",
          body: "Todo movimiento reportado tiene carácter preliminar hasta su verificación. Los movimientos deben ser confirmados antes de incorporarse al registro maestro. Mientras no exista confirmación, el movimiento no se considera parte del registro oficial y no debe utilizarse como respaldo definitivo de una operación.",
        },
        {
          title: "5. Uso adecuado del canal",
          body: "El canal debe emplearse únicamente para los fines administrativos previstos. No debe utilizarse para enviar contenido ajeno a la operación, información de terceros sin autorización, ni material ilícito, ofensivo o que vulnere derechos de otras personas.",
        },
        {
          title: "6. Disponibilidad del servicio",
          body: "El sistema depende de servicios tecnológicos de terceros y puede presentar interrupciones, mantenimientos o cambios funcionales. La empresa no garantiza disponibilidad ininterrumpida y podrá modificar, suspender o descontinuar el sistema cuando lo considere necesario para su operación.",
        },
        {
          title: "7. Tratamiento de datos",
          body: "El tratamiento de los datos procesados por el sistema se rige por la Política de Privacidad publicada en este sitio, la cual forma parte integrante de estos términos.",
        },
        {
          title: "8. Modificaciones",
          body: "Estos términos pueden ser actualizados en cualquier momento. La versión vigente será siempre la publicada en esta página, identificada con su fecha de última actualización.",
        },
      ],
      contactTitle: "Contacto",
      contactIntro: "Para consultas sobre estos términos, escríbanos a:",
      backHome: "Volver al inicio",
    },
    deletion: {
      eyebrow: "Información Legal",
      title: "Eliminar Datos",
      updated: "Última actualización: 27 de agosto de 2026",
      intro:
        "Usted puede solicitar la eliminación de los datos personales que el sistema de Granzonera y Agregados San Antonio, C.A. (GRANSACA) haya recibido a través del WhatsApp financiero. A continuación se explica cómo hacerlo y qué ocurre con su solicitud.",
      sections: [
        {
          title: "1. Cómo solicitar la eliminación",
          body: "Existen dos vías para solicitar la eliminación de sus datos. Puede utilizar la que le resulte más cómoda:",
          items: [
            "Enviar un correo electrónico a antoniocioffir@hotmail.com solicitando la eliminación de sus datos.",
            "Enviar el mensaje “ELIMINAR MIS DATOS” al WhatsApp financiero de la empresa.",
          ],
        },
        {
          title: "2. Información que debe incluir",
          body: "Para poder identificar correctamente sus registros, la solicitud debe incluir obligatoriamente los siguientes datos:",
          items: [
            "Su nombre.",
            "Su número telefónico (el mismo desde el cual escribió al WhatsApp financiero).",
          ],
        },
        {
          title: "3. Revisión y procesamiento",
          body: "Toda solicitud recibida será revisada y procesada. Verificaremos la identidad del solicitante y la correspondencia entre el nombre y el número telefónico indicados, con el fin de evitar eliminaciones indebidas de registros pertenecientes a otra persona.",
        },
        {
          title: "4. Registros que deben conservarse",
          body: "La eliminación se aplicará a todos los datos personales asociados a su solicitud, salvo aquellos registros que deban conservarse por obligaciones legales o contables. En esos casos, la información se mantiene únicamente para cumplir dichas obligaciones, con acceso restringido y sin utilizarse para ninguna otra finalidad.",
        },
        {
          title: "5. Confirmación",
          body: "Una vez procesada la solicitud, se le informará por el mismo canal utilizado para presentarla, indicando qué datos fueron eliminados y, si corresponde, qué registros deben permanecer por obligación legal o contable.",
        },
      ],
      contactTitle: "Canal de solicitudes",
      contactIntro: "Envíe su solicitud de eliminación de datos a:",
      backHome: "Volver al inicio",
    },
  },
  en: {
    labels: {
      privacy: "Privacy Policy",
      terms: "Terms of Service",
      deletion: "Delete Data",
      legalTitle: "Legal",
    },
    privacy: {
      eyebrow: "Legal Information",
      title: "Privacy Policy",
      updated: "Last updated: August 27, 2026",
      intro:
        "The data controller is Granzonera y Agregados San Antonio, C.A. (GRANSACA). This policy explains what information our administrative record system receives and processes, the purpose for which we use it, which technology services process it and how you can exercise your rights over your data.",
      sections: [
        {
          title: "1. Data controller",
          body: "The controller responsible for processing the personal data described in this policy is Granzonera y Agregados San Antonio, C.A. (GRANSACA), a company dedicated to the production and marketing of construction aggregates in Venezuela. Any inquiry regarding this policy may be sent to the email address listed in the contact section.",
        },
        {
          title: "2. Source of the information",
          body: "The system only receives messages voluntarily sent by the user to the company's financial WhatsApp. We do not collect data automatically from other sources, nor do we acquire third-party databases. If you do not send messages to that channel, the system does not process any information about you.",
        },
        {
          title: "3. Data processed",
          body: "Based on the messages sent to the financial WhatsApp, the system may process and store the following data:",
          items: [
            "User name.",
            "Phone number associated with the message.",
            "Content of the messages sent.",
            "Sales records.",
            "Expense records.",
            "Payment records and payment methods.",
            "Balances derived from those records.",
          ],
        },
        {
          title: "4. Purpose of processing",
          body: "The data is used exclusively for the company's internal administrative, accounting and financial control. This includes recording transactions, reconciling payments, tracking sales and expenses and preparing internal balances. It is not used for any purpose other than the one declared here.",
        },
        {
          title: "5. Technology services used",
          body: "To receive, process and store the information, the system may rely on the following third-party technology services, each with its own privacy and security policies:",
          items: [
            "Meta / WhatsApp — receipt of messages sent to the financial WhatsApp.",
            "Make — automation of the message processing workflow.",
            "Google Sheets — structured storage of administrative records.",
            "OpenAI — interpretation and structuring of message content.",
          ],
        },
        {
          title: "6. No sale of data",
          body: "We do not sell, rent or transfer data to third parties for commercial purposes. We also do not use the information for advertising, audience segmentation, direct marketing or promotional profiling of any kind.",
        },
        {
          title: "7. Security and restricted access",
          body: "Access to the information is restricted to authorized company personnel who require the data to perform administrative, accounting or financial duties. Reasonable access control, authentication and safeguarding measures are applied. No internet transmission method is completely infallible, so absolute security cannot be guaranteed.",
        },
        {
          title: "8. Retention period",
          body: "Administrative, accounting and financial records are retained for as long as the commercial or operational relationship continues and, thereafter, for the period required by applicable accounting, tax and legal obligations in Venezuela. Message content that is not necessary to support an accounting record is deleted or anonymized once it is no longer required for the purpose that prompted its receipt.",
        },
        {
          title: "9. User rights",
          body: "You may request access to your data, its correction or update, as well as its deletion, at any time. To do so, send your request to the contact email, stating your name and the phone number from which you wrote to the financial WhatsApp. The request will be reviewed and processed, except for records that must be retained due to legal or accounting obligations.",
        },
        {
          title: "10. Changes to this policy",
          body: "This policy may be updated to reflect operational, technological or regulatory changes. Any modification will be published on this same page, together with its last update date.",
        },
      ],
      contactTitle: "Privacy contact",
      contactIntro: "To exercise your rights or ask about this policy, write to us at:",
      backHome: "Back to home",
    },
    terms: {
      eyebrow: "Legal Information",
      title: "Terms of Service",
      updated: "Last updated: August 27, 2026",
      intro:
        "The administrative record system of Granzonera y Agregados San Antonio, C.A. (GRANSACA) is an internal tool with restricted access. These terms describe its purpose, who may use it and the responsibilities of each authorized user.",
      sections: [
        {
          title: "1. Nature of the system",
          body: "The system is an internal administrative, accounting and financial record tool. Its function is to receive, organize and consolidate the company's operational transactions. It is not a commercial service offered to the public, nor a payment platform, nor an accounting, tax or financial advisory system.",
        },
        {
          title: "2. Use restricted to authorized persons",
          body: "Only persons expressly authorized by the company may use the system to record or review transactions. Access is personal and non-transferable. Sharing credentials or allowing unauthorized third parties to use the channel is prohibited. The company may revoke authorization at any time.",
        },
        {
          title: "3. Responsibility for submitted information",
          body: "Each user is responsible for providing correct, complete and timely information. This includes amounts, dates, descriptions, payment methods and any data needed to identify the transaction. Users must refrain from recording false or duplicated information, or entries that do not correspond to actual company operations.",
        },
        {
          title: "4. Confirmation of transactions",
          body: "Every reported transaction is preliminary until verified. Transactions must be confirmed before being incorporated into the master record. Until confirmation exists, a transaction is not considered part of the official record and must not be used as definitive support for an operation.",
        },
        {
          title: "5. Appropriate use of the channel",
          body: "The channel must be used solely for its intended administrative purposes. It must not be used to send content unrelated to operations, third-party information without authorization, or unlawful, offensive material or material that infringes the rights of others.",
        },
        {
          title: "6. Service availability",
          body: "The system depends on third-party technology services and may experience interruptions, maintenance or functional changes. The company does not guarantee uninterrupted availability and may modify, suspend or discontinue the system whenever it deems necessary for its operation.",
        },
        {
          title: "7. Data processing",
          body: "The processing of data handled by the system is governed by the Privacy Policy published on this site, which forms an integral part of these terms.",
        },
        {
          title: "8. Modifications",
          body: "These terms may be updated at any time. The version in force will always be the one published on this page, identified by its last update date.",
        },
      ],
      contactTitle: "Contact",
      contactIntro: "For questions about these terms, write to us at:",
      backHome: "Back to home",
    },
    deletion: {
      eyebrow: "Legal Information",
      title: "Delete Data",
      updated: "Last updated: August 27, 2026",
      intro:
        "You may request the deletion of the personal data that the system of Granzonera y Agregados San Antonio, C.A. (GRANSACA) has received through the financial WhatsApp. Below is how to do it and what happens with your request.",
      sections: [
        {
          title: "1. How to request deletion",
          body: "There are two ways to request the deletion of your data. Use whichever is most convenient for you:",
          items: [
            "Send an email to antoniocioffir@hotmail.com requesting the deletion of your data.",
            "Send the message “ELIMINAR MIS DATOS” to the company's financial WhatsApp.",
          ],
        },
        {
          title: "2. Information you must include",
          body: "So that we can correctly identify your records, the request must include the following details:",
          items: [
            "Your name.",
            "Your phone number (the same one from which you wrote to the financial WhatsApp).",
          ],
        },
        {
          title: "3. Review and processing",
          body: "Every request received will be reviewed and processed. We will verify the identity of the requester and the match between the name and phone number provided, in order to prevent the improper deletion of records belonging to another person.",
        },
        {
          title: "4. Records that must be retained",
          body: "Deletion will apply to all personal data associated with your request, except for records that must be retained due to legal or accounting obligations. In those cases, the information is kept solely to comply with those obligations, with restricted access and without being used for any other purpose.",
        },
        {
          title: "5. Confirmation",
          body: "Once the request has been processed, you will be informed through the same channel used to submit it, indicating which data was deleted and, where applicable, which records must remain due to a legal or accounting obligation.",
        },
      ],
      contactTitle: "Request channel",
      contactIntro: "Send your data deletion request to:",
      backHome: "Back to home",
    },
  },
};

export { LEGAL_EMAIL };
